import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Pagination, PaginationItem, PaginationLink } from "reactstrap";
import {
    RebelColors,
    RebelCard,
    RebelSectionTitle,
    RebelButton,
    RebelAccentButton,
    RebelSecondaryButton,
    RebelDangerButton,
    RebelGrid,
    RebelListItem,
    RebelIcon,
    RebelEmptyState,
    RebelLoadingSpinner,
    RebelRow,
    RebelCol,
    RebelModal,
    RebelModalHeader,
    RebelModalBody,
    RebelModalFooter
} from "../../../styles/rebel-experiences";
import { GetDB, generateGUID } from "../../../utils/database/firebase-budget-tool";
import { listenToCollection, setDocument, updateDocumentProperty, deleteDocument } from "../../../utils/firestore-functions";
import firebase from "firebase/compat/app";

const RebelVillaList = () => {
    const [villaList, setVillaList] = useState({});
    const [sortedList, setSortedList] = useState({});
    const [deleteID, setDeleteID] = useState("");
    const [loading, setLoading] = useState(true);
    const [devMode, setDevMode] = useState(false);

    const budgetDB = GetDB();

    const [currentPage, setCurrentPage] = useState(0);
    const [recordsPerPage] = useState(12);

    const nPages = Math.ceil(Object.entries(sortedList).length / recordsPerPage);

    useEffect(() => {
        var unsubscribe = listenToCollection(budgetDB, "rebelVillas", SetLists);
        return unsubscribe;
    }, []);

    const SetLists = (villas) => {
        let x = villas.reduce((accumulator, value) => {
            return { ...accumulator, [value.id]: value }
        }, {});
        setVillaList(x);
        setSortedList(Object.fromEntries(Object.entries(x).sort(function (a, b) {
            return b[1].lastModification - a[1].lastModification;
        })));
        setLoading(false);
    };

    const openVilla = (id) => {
        const timeStamp = firebase.firestore.Timestamp.now().toDate();
        updateDocumentProperty(budgetDB, "rebelVillas", id, "lastModification", timeStamp);
        window.location = "/budget-app/rebel-settings/edit/" + id;
    };

    const createNewVilla = () => {
        const newTitle = "Property " + (Object.keys(villaList).length + 1);
        const timeStamp = firebase.firestore.Timestamp.now().toDate();

        let newVilla = {
            villaName: newTitle,
            location: "",
            lastModification: timeStamp,
            id: generateGUID(),
            destCurrency: "USD$",
            baseRate: 1,
            convertRate: 1,
            villaRentalFee: 0,
            cleaningFee: 0,
            flightCost: 0,
            costPerTransportation: 0,
            transportation: {},
            costPerActivity: 0,
            numActivities: 0,
            costPerBreakfast: 0,
            numBreakfast: 0,
            costPerLunch: 0,
            numLunch: 0,
            costPerDinner: 0,
            numDinner: 0,
            costPerCoffee: 0,
            numCoffee: 0,
            costPerAlcohol: 0,
            numAlcohol: 0,
            av: {value: 0, pp: "per day"},
            setupFee: {value: 0, pp: "per day"},
            roomRental: {value: 0, pp: "per day"},
            numMeetings: 0,
            miscellaneousCosts: [],
            notes: "",
            basicViewPricing: {}
        };

        const openNewVilla = () => {
            openVilla(newVilla.id);
        };

        setDocument(budgetDB, "rebelVillas", newVilla.id, newVilla, openNewVilla);
    };

    const deleteVilla = async (id) => {
        toggleDeleteModal();
        console.log("deleting " + deleteID);
        deleteDocument(budgetDB, "rebelVillas", deleteID);
    };

    const confirmDelete = (id) => {
        toggleDeleteModal();
        setDeleteID(id);
    };

    const [modal, setModal] = useState(false);
    const toggleDeleteModal = () => setModal(!modal);

    useEffect(() => {
        const handleKeyPress = (e) => {
            if (modal && e.key === 'Enter') {
                deleteVilla(deleteID);
            }
        };

        if (modal) {
            window.addEventListener('keydown', handleKeyPress);
        }

        return () => {
            window.removeEventListener('keydown', handleKeyPress);
        };
    }, [modal, deleteID]);

    const navigateToPage = (e, i) => {
        e.preventDefault();
        setCurrentPage(i);
    };

    if (loading) {
        return (
            <RebelCard>
                <div style={{ textAlign: 'center', padding: '48px' }}>
                    <RebelLoadingSpinner />
                    <p style={{ marginTop: '16px', color: '#b0b0b0' }}>Loading R.E.B.E.L Properties...</p>
                </div>
            </RebelCard>
        );
    }

    return (
        <React.Fragment>
            <RebelModal isOpen={modal} toggle={toggleDeleteModal}>
                <RebelModalHeader toggle={toggleDeleteModal}>
                    Delete R.E.B.E.L Property?
                </RebelModalHeader>
                <RebelModalBody>
                    This action cannot be undone. All data associated with this property will be permanently removed.
                </RebelModalBody>
                <RebelModalFooter>
                    <RebelSecondaryButton onClick={toggleDeleteModal}>
                        Cancel
                    </RebelSecondaryButton>
                    <RebelDangerButton onClick={() => deleteVilla(deleteID)}>
                        <RebelIcon icon={['fas', 'trash']} />
                        Delete Property
                    </RebelDangerButton>
                </RebelModalFooter>
            </RebelModal>

            <RebelCard>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                    <RebelSectionTitle>
                        <RebelIcon icon={['fas', 'home']} />
                        Properties
                    </RebelSectionTitle>
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                        <RebelAccentButton onClick={createNewVilla}>
                            <RebelIcon icon={['fas', 'plus']} />
                            Create New Property
                        </RebelAccentButton>
                        <DevButton
                            onClick={() => setDevMode(!devMode)}
                            $active={devMode}
                        >
                            Dev
                        </DevButton>
                    </div>
                </div>
                {devMode && (
                    <DevInfoPanel>
                        <DevInfoTitle>Debug Info</DevInfoTitle>
                        <DevInfoRow>
                            <DevInfoLabel>Database:</DevInfoLabel>
                            <DevInfoValue>rebelVillas</DevInfoValue>
                        </DevInfoRow>
                        <DevInfoRow>
                            <DevInfoLabel>Total Properties:</DevInfoLabel>
                            <DevInfoValue>{Object.keys(sortedList).length}</DevInfoValue>
                        </DevInfoRow>
                    </DevInfoPanel>
                )}

                {Object.entries(sortedList).length === 0 ? (
                    <RebelEmptyState>
                        <RebelIcon icon={['fas', 'home']} style={{ fontSize: '3rem', marginBottom: '16px' }} />
                        <h3>No R.E.B.E.L Properties Yet</h3>
                        <p>Create your first property to get started.</p>
                        <RebelAccentButton onClick={createNewVilla}>
                            <RebelIcon icon={['fas', 'plus']} />
                            Create Your First Property
                        </RebelAccentButton>
                    </RebelEmptyState>
                ) : (
                    <>
                        <RebelGrid>
                            {Object.entries(sortedList)
                                .slice(currentPage * recordsPerPage, (currentPage + 1) * recordsPerPage)
                                .map(([id, villa], i) => (
                                    <RebelListItem key={id}>
                                        <div style={{ marginBottom: '12px' }}>
                                            <h4 style={{ color: '#ffffff', marginBottom: '8px', fontSize: '1.1rem' }}>
                                                {villa.villaName}
                                            </h4>
                                            <p style={{ color: '#b0b0b0', fontSize: '0.9rem', margin: '0' }}>
                                                {villa.location && (
                                                    <>
                                                        <RebelIcon icon={['fas', 'map-marker-alt']} />
                                                        {villa.location}
                                                    </>
                                                )}
                                            </p>
                                            <p style={{ color: '#b0b0b0', fontSize: '0.85rem', margin: '4px 0 0 0' }}>
                                                Modified: {villa.lastModification ?
                                                    villa.lastModification.toDate().toLocaleDateString("en-US") :
                                                    'Unknown'
                                                }
                                            </p>
                                            {devMode && (
                                                <PropertyDevInfo>
                                                    <DevInfoRow>
                                                        <DevInfoLabel>ID:</DevInfoLabel>
                                                        <DevInfoValue>{villa.id}</DevInfoValue>
                                                    </DevInfoRow>
                                                </PropertyDevInfo>
                                            )}
                                        </div>

                                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                                            <RebelAccentButton
                                                size="sm"
                                                onClick={() => openVilla(villa.id)}
                                                style={{ padding: '8px 16px', fontSize: '0.85rem' }}
                                            >
                                                <RebelIcon icon={['fas', 'edit']} />
                                                Edit
                                            </RebelAccentButton>
                                            <RebelSecondaryButton
                                                size="sm"
                                                onClick={() => confirmDelete(villa.id)}
                                                style={{
                                                    padding: '8px 12px',
                                                    fontSize: '0.85rem',
                                                    border: '2px solid #dc3545',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    minWidth: '44px'
                                                }}
                                            >
                                                <RebelIcon icon={['fas', 'trash']} style={{marginRight: 0}} />
                                            </RebelSecondaryButton>
                                        </div>
                                    </RebelListItem>
                                ))}
                        </RebelGrid>

                        {nPages > 1 && (
                            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '32px' }}>
                                <RebelPagination>
                                    <RebelPaginationItem disabled={currentPage <= 0}>
                                        <RebelPaginationLink
                                            onClick={e => navigateToPage(e, 0)}
                                            first
                                            href="#"
                                        />
                                    </RebelPaginationItem>
                                    <RebelPaginationItem disabled={currentPage <= 0}>
                                        <RebelPaginationLink
                                            onClick={e => navigateToPage(e, currentPage - 1)}
                                            href="#"
                                            previous
                                        />
                                    </RebelPaginationItem>
                                    {[...Array(nPages)].map((page, i) =>
                                        <RebelPaginationItem active={i === currentPage} key={i}>
                                            <RebelPaginationLink
                                                active={i === currentPage}
                                                onClick={e => navigateToPage(e, i)}
                                                href="#"
                                            >
                                                {i + 1}
                                            </RebelPaginationLink>
                                        </RebelPaginationItem>
                                    )}
                                    <RebelPaginationItem disabled={currentPage + 1 >= nPages}>
                                        <RebelPaginationLink
                                            onClick={e => navigateToPage(e, currentPage + 1)}
                                            href="#"
                                            next
                                        />
                                    </RebelPaginationItem>
                                    <RebelPaginationItem disabled={currentPage + 1 >= nPages}>
                                        <RebelPaginationLink
                                            onClick={e => navigateToPage(e, nPages - 1)}
                                            href="#"
                                            last
                                        />
                                    </RebelPaginationItem>
                                </RebelPagination>
                            </div>
                        )}
                    </>
                )}
            </RebelCard>
        </React.Fragment>
    );
};

export default RebelVillaList;

const RebelPagination = styled(Pagination)``;

const RebelPaginationItem = styled(PaginationItem)`
    ${props => props.active && `
        color: white;
        background-color: ${RebelColors.accent};
    `}
`;

const RebelPaginationLink = styled(PaginationLink)`
    background-color: ${RebelColors.surface} !important;
    border-color: ${RebelColors.border} !important;
    color: ${RebelColors.gold} !important;

    &:hover {
        background-color: ${RebelColors.surfaceLight} !important;
        color: ${RebelColors.gold} !important;
        border-color: ${RebelColors.gold} !important;
    }

    ${props => props.active && `
        color: #000000 !important;
        background-color: ${RebelColors.gold} !important;
        border-color: ${RebelColors.gold} !important;
    `}
`;

const DevButton = styled.button`
    background-color: ${props => props.$active ? '#4a4a4a' : 'transparent'};
    border: 1px solid #4a4a4a;
    border-radius: 4px;
    padding: 8px 12px;
    color: ${props => props.$active ? '#fff' : '#888'};
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        background-color: #4a4a4a;
        color: #fff;
    }
`;

const DevInfoPanel = styled.div`
    background-color: #1a1a2e;
    border: 1px solid #4a4a4a;
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 20px;
    font-family: monospace;
`;

const DevInfoTitle = styled.div`
    color: #00d9ff;
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 12px;
    text-transform: uppercase;
    letter-spacing: 1px;
`;

const DevInfoRow = styled.div`
    display: flex;
    align-items: center;
    margin-bottom: 8px;

    &:last-child {
        margin-bottom: 0;
    }
`;

const DevInfoLabel = styled.span`
    color: #888;
    font-size: 12px;
    min-width: 120px;
`;

const DevInfoValue = styled.span`
    color: #00ff88;
    font-size: 12px;
    background-color: #0a0a15;
    padding: 4px 8px;
    border-radius: 4px;
    user-select: all;
`;

const PropertyDevInfo = styled.div`
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px dashed #4a4a4a;
    font-family: monospace;
`;
